import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { BalanceApi } from "../../api/BalanceApi";
import type { BalanceFetchOptions } from "../../generated/dto/BalanceFetchOptions";
import type { BalanceLookupCooldownFetchOptions } from "../../generated/dto/BalanceLookupCooldownFetchOptions";
import type { BalanceLookupCooldownStaff } from "../../generated/dto/BalanceLookupCooldownStaff";
import type { BalanceMember } from "../../generated/dto/BalanceMember";
import type { BalanceMemberPage } from "../../generated/dto/BalanceMemberPage";
import type { BalanceSaveRequest } from "../../generated/dto/BalanceSaveRequest";
import type { BalanceSearchOptions } from "../../generated/dto/BalanceSearchOptions";
import type { BalanceSearchParams } from "../../generated/dto/BalanceSearchParams";
import type { BalanceStaff } from "../../generated/dto/BalanceStaff";
import type { BalanceStaffPage } from "../../generated/dto/BalanceStaffPage";
import type { BalanceStaffSearchResult } from "../../generated/dto/BalanceStaffSearchResult";
import type { BalanceTransactionFetchOptions } from "../../generated/dto/BalanceTransactionFetchOptions";
import type { BalanceTransactionStaff } from "../../generated/dto/BalanceTransactionStaff";
import type { PersonBalanceFetchOptions } from "../../generated/dto/PersonBalanceFetchOptions";
import type { PersonBalanceStaff } from "../../generated/dto/PersonBalanceStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class BalanceApiClient implements BalanceApi {
  constructor(private readonly client = useClient()) {}

  async getBalanceStaff(id: number, options?: BalanceFetchOptions): Promise<BalanceStaff> {
    const { data } = await this.client.get<unknown>(`/balance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.BalanceFetchOptions") });
    return deserializeCbiDto<BalanceStaff>(data, "generated.dto.BalanceStaff");
  }

  async getPersonBalanceStaff(id: number, options?: PersonBalanceFetchOptions): Promise<PersonBalanceStaff> {
    const { data } = await this.client.get<unknown>(`/personbalance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonBalanceFetchOptions") });
    return deserializeCbiDto<PersonBalanceStaff>(data, "generated.dto.PersonBalanceStaff");
  }

  async getBalanceLookupCooldownStaff(id: number, options?: BalanceLookupCooldownFetchOptions): Promise<BalanceLookupCooldownStaff> {
    const { data } = await this.client.get<unknown>(`/balancelookupcooldown/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.BalanceLookupCooldownFetchOptions") });
    return deserializeCbiDto<BalanceLookupCooldownStaff>(data, "generated.dto.BalanceLookupCooldownStaff");
  }

  async searchBalanceStaff(params: BalanceSearchParams, page: number, perPage: number): Promise<PaginationResult<BalanceStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/balance/staff/search`, serializeCbiDto(params, "generated.dto.BalanceSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<BalanceStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<BalanceStaffSearchResult>>(result.items, "generated.dto.BalanceStaffSearchResult") };
    return result;
  }

  async searchBalanceStaffFull(params: BalanceSearchParams, page: number, perPage: number, options?: BalanceFetchOptions): Promise<PaginationResult<BalanceStaff>> {
    const { data } = await this.client.post<unknown>(`/balance/staff/search/full`, serializeCbiDto(params, "generated.dto.BalanceSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.BalanceFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<BalanceStaff>;
    result = { ...result, items: deserializeCbiDto<Array<BalanceStaff>>(result.items, "generated.dto.BalanceStaff") };
    return result;
  }

  async getBalanceStaffBalanceOwner(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/balance/staff/${id}/balanceowner`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getBalanceStaffPersonBalances(id: number, options?: PersonBalanceFetchOptions): Promise<Array<PersonBalanceStaff>> {
    const { data } = await this.client.get<unknown>(`/balance/staff/${id}/personbalances`, { params: serializeCbiDto(options, "generated.dto.PersonBalanceFetchOptions") });
    return deserializeCbiDto<Array<PersonBalanceStaff>>(data, "generated.dto.PersonBalanceStaff");
  }

  async getBalanceStaffTransactions(id: number, options?: BalanceTransactionFetchOptions): Promise<Array<BalanceTransactionStaff>> {
    const { data } = await this.client.get<unknown>(`/balance/staff/${id}/transactions`, { params: serializeCbiDto(options, "generated.dto.BalanceTransactionFetchOptions") });
    return deserializeCbiDto<Array<BalanceTransactionStaff>>(data, "generated.dto.BalanceTransactionStaff");
  }

  async getPersonBalanceStaffBalanceId(id: number, options?: BalanceFetchOptions): Promise<BalanceStaff> {
    const { data } = await this.client.get<unknown>(`/personbalance/staff/${id}/balanceid`, { params: serializeCbiDto(options, "generated.dto.BalanceFetchOptions") });
    return deserializeCbiDto<BalanceStaff>(data, "generated.dto.BalanceStaff");
  }

  async getPersonBalanceStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personbalance/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getBalanceLookupCooldownStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/balancelookupcooldown/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getBalanceLookupCooldownStaffBalanceMatch(id: number, options?: BalanceFetchOptions): Promise<BalanceStaff | null> {
    const { data } = await this.client.get<unknown>(`/balancelookupcooldown/staff/${id}/balancematch`, { params: serializeCbiDto(options, "generated.dto.BalanceFetchOptions") });
    return deserializeCbiDto<BalanceStaff | null>(data, "generated.dto.BalanceStaff");
  }

  async balancesStaff(options: BalanceSearchOptions): Promise<BalanceStaffPage> {
    const { data } = await this.client.post<unknown>(`/balance/staff/balances`, serializeCbiDto(options, "generated.dto.BalanceSearchOptions"));
    return deserializeCbiDto<BalanceStaffPage>(data, "generated.dto.BalanceStaffPage");
  }

  async findBalanceByNumStaff(number: string): Promise<BalanceStaff> {
    const { data } = await this.client.post<unknown>(`/balance/staff/findbalancebynum`, JSON.stringify(number));
    let result = data as BalanceStaff;
    result = deserializeCbiDto<BalanceStaff>(result, "generated.dto.BalanceStaff");
    return result;
  }

  async findBalanceByNumMember(number: string): Promise<BalanceMember> {
    const { data } = await this.client.post<unknown>(`/balance/member/findbalancebynum`, JSON.stringify(number));
    let result = data as BalanceMember;
    result = deserializeCbiDto<BalanceMember>(result, "generated.dto.BalanceMember");
    return result;
  }

  async memberBalancesMember(options: BalanceSearchOptions): Promise<BalanceMemberPage> {
    const { data } = await this.client.post<unknown>(`/balance/member/memberbalances`, serializeCbiDto(options, "generated.dto.BalanceSearchOptions"));
    return deserializeCbiDto<BalanceMemberPage>(data, "generated.dto.BalanceMemberPage");
  }

  async saveBalanceStaff(request: BalanceSaveRequest): Promise<BalanceStaff> {
    const { data } = await this.client.post<unknown>(`/balance/staff/savebalance`, serializeCbiDto(request, "generated.dto.BalanceSaveRequest"));
    return deserializeCbiDto<BalanceStaff>(data, "generated.dto.BalanceStaff");
  }
}

export const useBalanceApi = (): BalanceApi => new BalanceApiClient();
